import React, { useState, useEffect } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Badge } from '../../ui/Badge';
import { colors, agentThemes, spacing, borderRadius } from '../../../constants/theme';

interface Campaign {
  id: string;
  name: string;
  type: 'discount' | 'points' | 'bundle' | 'seasonal' | 'loyalty';
  status: 'draft' | 'active' | 'scheduled' | 'completed';
  startDate: Date;
  endDate: Date;
  target: TargetAudience;
  budget: number;
  expectedROI: number;
  channels: Channel[];
  creatives: Creative[];
  metrics: CampaignMetrics;
}

interface TargetAudience {
  segments: string[];
  ageRange: { min: number; max: number };
  gender: 'all' | 'male' | 'female';
  interests: string[];
  expectedReach: number;
}

interface Channel {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'line' | 'web' | 'store' | 'social';
  budget: number;
  performance: {
    reach: number;
    engagement: number;
    conversion: number;
  };
}

interface Creative {
  id: string;
  type: 'banner' | 'email' | 'poster' | 'video';
  name: string;
  status: 'pending' | 'approved' | 'rejected';
  thumbnail?: string;
}

interface CampaignMetrics {
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
  cost: number;
}

interface CampaignPlannerProps {
  onCampaignCreate?: (campaign: Campaign) => void;
  onCampaignEdit?: (campaign: Campaign) => void;
  selectedCampaign?: Campaign;
}

const CampaignPlanner: React.FC<CampaignPlannerProps> = ({
  onCampaignCreate,
  onCampaignEdit,
  selectedCampaign
}) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'targeting' | 'creative' | 'schedule'>('overview');
  const [formData, setFormData] = useState({
    name: '',
    type: 'discount',
    startDate: '',
    endDate: '',
    budget: 100000,
    targetSegments: [] as string[],
    channels: [] as string[]
  });

  // サンプルキャンペーンデータ
  const sampleCampaigns: Campaign[] = [
    {
      id: 'camp001',
      name: '春の新生活応援セール',
      type: 'seasonal',
      status: 'active',
      startDate: new Date('2025-03-01'),
      endDate: new Date('2025-03-31'),
      target: {
        segments: ['新生活', '若年層', 'ファミリー'],
        ageRange: { min: 20, max: 45 },
        gender: 'all',
        interests: ['家具', '家電', '生活雑貨'],
        expectedReach: 25000
      },
      budget: 500000,
      expectedROI: 3.5,
      channels: [
        {
          id: 'ch1',
          name: 'LINE配信',
          type: 'line',
          budget: 150000,
          performance: { reach: 12000, engagement: 2400, conversion: 480 }
        },
        {
          id: 'ch2',
          name: '店頭POP',
          type: 'store',
          budget: 200000,
          performance: { reach: 8000, engagement: 3200, conversion: 640 }
        },
        {
          id: 'ch3',
          name: 'メール',
          type: 'email',
          budget: 150000,
          performance: { reach: 5000, engagement: 1000, conversion: 200 }
        }
      ],
      creatives: [
        { id: 'cr1', type: 'banner', name: 'メインビジュアル', status: 'approved' },
        { id: 'cr2', type: 'poster', name: '店頭ポスター', status: 'approved' },
        { id: 'cr3', type: 'email', name: 'メールテンプレート', status: 'pending' }
      ],
      metrics: {
        impressions: 125000,
        clicks: 6400,
        conversions: 1320,
        revenue: 1750000,
        cost: 450000
      }
    },
    {
      id: 'camp002',
      name: 'ポイント2倍キャンペーン',
      type: 'points',
      status: 'scheduled',
      startDate: new Date('2025-04-01'),
      endDate: new Date('2025-04-07'),
      target: {
        segments: ['リピーター', 'ロイヤル顧客'],
        ageRange: { min: 30, max: 60 },
        gender: 'all',
        interests: ['食品', '日用品'],
        expectedReach: 15000
      },
      budget: 300000,
      expectedROI: 2.8,
      channels: [
        {
          id: 'ch4',
          name: 'アプリプッシュ',
          type: 'web',
          budget: 100000,
          performance: { reach: 8000, engagement: 2000, conversion: 400 }
        }
      ],
      creatives: [
        { id: 'cr4', type: 'banner', name: 'アプリバナー', status: 'approved' }
      ],
      metrics: {
        impressions: 0,
        clicks: 0,
        conversions: 0,
        revenue: 0,
        cost: 0
      }
    }
  ];

  useEffect(() => {
    setCampaigns(sampleCampaigns);
  }, []);

  const getStatusColor = (status: Campaign['status']) => {
    const colorMap = {
      draft: colors.text.light,
      active: colors.status.success,
      scheduled: colors.accent.blue,
      completed: colors.text.secondary
    };
    return colorMap[status];
  };

  const getStatusText = (status: Campaign['status']) => {
    const textMap = {
      draft: '下書き',
      active: '実施中',
      scheduled: '予定',
      completed: '完了'
    };
    return textMap[status];
  };

  const getCampaignTypeIcon = (type: Campaign['type']) => {
    const iconMap = {
      discount: '🏷️',
      points: '⭐',
      bundle: '🎁',
      seasonal: '🌸',
      loyalty: '💝'
    };
    return iconMap[type];
  };

  const getChannelIcon = (type: Channel['type']) => {
    const iconMap = {
      email: '📧',
      sms: '💬',
      line: '💚',
      web: '🌐',
      store: '🏪',
      social: '📱'
    };
    return iconMap[type];
  };

  const formatCurrency = (amount: number) => {
    return `¥${amount.toLocaleString('ja-JP')}`;
  };

  const calculateROI = (metrics: CampaignMetrics) => {
    if (metrics.cost === 0) return 0;
    return ((metrics.revenue - metrics.cost) / metrics.cost * 100).toFixed(1);
  };

  return (
    <div style={{ padding: spacing.lg }}>
      {/* ヘッダー */}
      <div style={{ marginBottom: spacing.lg }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: spacing.md
        }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 600, 
            color: colors.text.dark,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm
          }}>
            <span>{agentThemes.marketing.icon}</span>
            キャンペーンプランナー
          </h2>
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            style={{
              backgroundColor: agentThemes.marketing.primary,
              color: '#FFFFFF'
            }}
          >
            新規キャンペーン作成
          </Button>
        </div>
      </div>

      {/* 新規作成フォーム */}
      {showCreateForm && (
        <Card style={{ 
          marginBottom: spacing.lg,
          border: `2px solid ${agentThemes.marketing.primary}`
        }}>
          <div style={{ padding: spacing.lg }}>
            <h3 style={{ 
              fontSize: '1.25rem',
              fontWeight: 600,
              color: colors.text.dark,
              marginBottom: spacing.md
            }}>
              新規キャンペーン作成
            </h3>

            {/* タブナビゲーション */}
            <div style={{ 
              display: 'flex',
              gap: spacing.sm,
              marginBottom: spacing.lg,
              borderBottom: `1px solid ${colors.support.gray}`,
              paddingBottom: spacing.sm
            }}>
              {(['overview', 'targeting', 'creative', 'schedule'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setSelectedTab(tab)}
                  style={{
                    padding: `${spacing.sm} ${spacing.md}`,
                    background: 'none',
                    border: 'none',
                    color: selectedTab === tab ? agentThemes.marketing.primary : colors.text.light,
                    fontWeight: selectedTab === tab ? 600 : 400,
                    borderBottom: selectedTab === tab ? `2px solid ${agentThemes.marketing.primary}` : 'none',
                    cursor: 'pointer',
                    transition: 'all 200ms ease'
                  }}
                >
                  {tab === 'overview' && '基本情報'}
                  {tab === 'targeting' && 'ターゲティング'}
                  {tab === 'creative' && 'クリエイティブ'}
                  {tab === 'schedule' && 'スケジュール'}
                </button>
              ))}
            </div>

            {/* タブコンテンツ */}
            <div>
              {selectedTab === 'overview' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
                  <div>
                    <label style={{ 
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: colors.text.dark,
                      marginBottom: spacing.xs
                    }}>
                      キャンペーン名
                    </label>
                    <Input
                      placeholder="例: 春の新生活応援セール"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: colors.text.dark,
                      marginBottom: spacing.xs
                    }}>
                      キャンペーンタイプ
                    </label>
                    <Select
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      style={{ width: '100%' }}
                    >
                      <option value="discount">割引セール</option>
                      <option value="points">ポイントキャンペーン</option>
                      <option value="bundle">バンドル販売</option>
                      <option value="seasonal">季節キャンペーン</option>
                      <option value="loyalty">ロイヤリティ</option>
                    </Select>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: colors.text.dark,
                      marginBottom: spacing.xs
                    }}>
                      予算
                    </label>
                    <Input
                      type="number"
                      placeholder="100000"
                      value={formData.budget}
                      onChange={(e) => setFormData({...formData, budget: parseInt(e.target.value)})}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              )}

              {selectedTab === 'targeting' && (
                <div>
                  <p style={{ color: colors.text.light, marginBottom: spacing.md }}>
                    ターゲットセグメントと配信チャネルを設定してください
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg }}>
                    <div>
                      <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: spacing.sm }}>
                        ターゲットセグメント
                      </h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs }}>
                        {['新規顧客', 'リピーター', 'VIP', '若年層', 'ファミリー', 'シニア'].map(segment => (
                          <Badge
                            key={segment}
                            variant={formData.targetSegments.includes(segment) ? 'primary' : 'secondary'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              const newSegments = formData.targetSegments.includes(segment)
                                ? formData.targetSegments.filter(s => s !== segment)
                                : [...formData.targetSegments, segment];
                              setFormData({...formData, targetSegments: newSegments});
                            }}
                          >
                            {segment}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: spacing.sm }}>
                        配信チャネル
                      </h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs }}>
                        {['LINE', 'メール', 'SMS', 'アプリ', '店頭', 'SNS'].map(channel => (
                          <Badge
                            key={channel}
                            variant={formData.channels.includes(channel) ? 'primary' : 'secondary'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              const newChannels = formData.channels.includes(channel)
                                ? formData.channels.filter(c => c !== channel)
                                : [...formData.channels, channel];
                              setFormData({...formData, channels: newChannels});
                            }}
                          >
                            {channel}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedTab === 'creative' && (
                <div>
                  <p style={{ color: colors.text.light, marginBottom: spacing.md }}>
                    キャンペーンで使用するクリエイティブを管理します
                  </p>
                  <div style={{ 
                    border: `2px dashed ${colors.support.gray}`,
                    borderRadius: borderRadius.md,
                    padding: spacing.xl,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 200ms ease'
                  }}>
                    <div style={{ fontSize: '2rem', marginBottom: spacing.sm }}>📤</div>
                    <div style={{ color: colors.text.dark, fontWeight: 500 }}>
                      クリエイティブをアップロード
                    </div>
                    <div style={{ color: colors.text.light, fontSize: '0.875rem' }}>
                      またはドラッグ&ドロップ
                    </div>
                  </div>
                </div>
              )}

              {selectedTab === 'schedule' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
                  <div>
                    <label style={{ 
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: colors.text.dark,
                      marginBottom: spacing.xs
                    }}>
                      開始日
                    </label>
                    <Input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: colors.text.dark,
                      marginBottom: spacing.xs
                    }}>
                      終了日
                    </label>
                    <Input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* アクションボタン */}
            <div style={{ 
              display: 'flex',
              justifyContent: 'flex-end',
              gap: spacing.sm,
              marginTop: spacing.lg,
              paddingTop: spacing.md,
              borderTop: `1px solid ${colors.support.gray}`
            }}>
              <Button
                variant="outline"
                onClick={() => setShowCreateForm(false)}
              >
                キャンセル
              </Button>
              <Button
                style={{
                  backgroundColor: agentThemes.marketing.primary,
                  color: '#FFFFFF'
                }}
                onClick={() => {
                  // キャンペーン作成処理
                  setShowCreateForm(false);
                }}
              >
                作成する
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* キャンペーン一覧 */}
      <div style={{ display: 'grid', gap: spacing.md }}>
        {campaigns.map(campaign => (
          <Card key={campaign.id} style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex' }}>
              {/* 左側: キャンペーン情報 */}
              <div style={{ flex: 1, padding: spacing.lg }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
                      <span style={{ fontSize: '1.5rem' }}>
                        {getCampaignTypeIcon(campaign.type)}
                      </span>
                      <h3 style={{ 
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        color: colors.text.dark
                      }}>
                        {campaign.name}
                      </h3>
                      <Badge 
                        style={{
                          backgroundColor: getStatusColor(campaign.status),
                          color: '#FFFFFF'
                        }}
                      >
                        {getStatusText(campaign.status)}
                      </Badge>
                    </div>
                    <div style={{ 
                      color: colors.text.light,
                      fontSize: '0.875rem',
                      marginBottom: spacing.md
                    }}>
                      {campaign.startDate.toLocaleDateString('ja-JP')} ～ {campaign.endDate.toLocaleDateString('ja-JP')}
                    </div>
                  </div>
                </div>

                {/* ターゲット情報 */}
                <div style={{ marginBottom: spacing.md }}>
                  <div style={{ 
                    fontSize: '0.875rem',
                    color: colors.text.secondary,
                    marginBottom: spacing.xs
                  }}>
                    ターゲット
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs }}>
                    {campaign.target.segments.map(segment => (
                      <Badge key={segment} variant="secondary" size="sm">
                        {segment}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* チャネル */}
                <div style={{ marginBottom: spacing.md }}>
                  <div style={{ 
                    fontSize: '0.875rem',
                    color: colors.text.secondary,
                    marginBottom: spacing.xs
                  }}>
                    配信チャネル
                  </div>
                  <div style={{ display: 'flex', gap: spacing.md }}>
                    {campaign.channels.map(channel => (
                      <div key={channel.id} style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                        <span>{getChannelIcon(channel.type)}</span>
                        <span style={{ fontSize: '0.875rem', color: colors.text.dark }}>
                          {channel.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* アクション */}
                <div style={{ display: 'flex', gap: spacing.sm }}>
                  <Button size="sm" variant="outline">
                    編集
                  </Button>
                  <Button size="sm" variant="outline">
                    複製
                  </Button>
                  <Button size="sm" variant="outline">
                    レポート
                  </Button>
                </div>
              </div>

              {/* 右側: メトリクス */}
              <div style={{ 
                width: '320px',
                padding: spacing.lg,
                backgroundColor: colors.background.secondary,
                borderLeft: `1px solid ${colors.support.gray}`
              }}>
                <h4 style={{ 
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: colors.text.dark,
                  marginBottom: spacing.md
                }}>
                  パフォーマンス
                </h4>

                {campaign.status === 'active' || campaign.status === 'completed' ? (
                  <>
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: spacing.md,
                      marginBottom: spacing.md
                    }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: colors.text.light }}>
                          売上
                        </div>
                        <div style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.text.dark }}>
                          {formatCurrency(campaign.metrics.revenue)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: colors.text.light }}>
                          コスト
                        </div>
                        <div style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.text.dark }}>
                          {formatCurrency(campaign.metrics.cost)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: colors.text.light }}>
                          ROI
                        </div>
                        <div style={{ 
                          fontSize: '1.125rem',
                          fontWeight: 600,
                          color: parseFloat(calculateROI(campaign.metrics)) > 0 
                            ? colors.status.success
                            : colors.status.error
                        }}>
                          {calculateROI(campaign.metrics)}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: colors.text.light }}>
                          CVR
                        </div>
                        <div style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.text.dark }}>
                          {campaign.metrics.clicks > 0 
                            ? ((campaign.metrics.conversions / campaign.metrics.clicks) * 100).toFixed(1) 
                            : '0'}%
                        </div>
                      </div>
                    </div>

                    <div style={{ 
                      paddingTop: spacing.md,
                      borderTop: `1px solid ${colors.support.gray}`
                    }}>
                      <div style={{ marginBottom: spacing.sm }}>
                        <div style={{ 
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.875rem'
                        }}>
                          <span style={{ color: colors.text.light }}>予算消化率</span>
                          <span style={{ fontWeight: 600, color: colors.text.dark }}>
                            {((campaign.metrics.cost / campaign.budget) * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div style={{
                          marginTop: spacing.xs,
                          backgroundColor: colors.support.gray,
                          borderRadius: '4px',
                          height: '6px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${Math.min((campaign.metrics.cost / campaign.budget) * 100, 100)}%`,
                            height: '100%',
                            backgroundColor: agentThemes.marketing.primary,
                            transition: 'width 500ms ease'
                          }} />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ 
                    textAlign: 'center',
                    padding: spacing.xl,
                    color: colors.text.light
                  }}>
                    <div style={{ fontSize: '2rem', marginBottom: spacing.sm }}>📊</div>
                    <div style={{ fontSize: '0.875rem' }}>
                      {campaign.status === 'scheduled' 
                        ? 'キャンペーン開始後にデータが表示されます'
                        : 'データがありません'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CampaignPlanner;